if (!customElements.get('ks-header')) {
  customElements.define(
    'ks-header',
    class KSHeader extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.main = this.querySelector('[data-ks-header-main]');
        this.utility = this.querySelector('.ks-header__utility');
        this.openButton = this.querySelector('[data-ks-menu-open]');
        this.closeButton = this.querySelector('[data-ks-menu-close]');
        this.drawer = this.querySelector('[data-ks-drawer]');
        this.drawerPanel = this.querySelector('[data-ks-drawer-panel]');
        this.lastFocusedElement = null;
        this.lastSubmenuButton = null;
        this.scrollFrame = null;
        this.closeTimer = null;
        this.stickyTransitionTimer = null;
        this.stickyEnterThreshold = 32;
        this.stickyExitThreshold = 4;
        this.isSticky = false;
        this.headerSection = this.closest('.shopify-section');

        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.onScroll = this.requestStickyUpdate.bind(this);

        this.openButton?.addEventListener('click', () => this.openDrawer());
        this.closeButton?.addEventListener('click', () => this.closeDrawer());
        this.drawer?.addEventListener('click', (event) => {
          if (!this.drawerPanel?.contains(event.target)) this.closeDrawer();
        });

        this.querySelectorAll('[data-ks-submenu-toggle]').forEach((button) => {
          button.addEventListener('click', () => this.toggleDesktopSubmenu(button));
        });

        this.querySelectorAll('[data-ks-mobile-submenu-toggle]').forEach((button) => {
          button.addEventListener('click', () => this.toggleMobileSubmenu(button));
        });

        this.drawer?.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', () => this.closeDrawer(false));
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize);

        if (this.dataset.sticky === 'true') {
          this.headerSection?.classList.add('ks-header-section--sticky');
          this.syncStickyMetrics();
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.updateStickyState(true);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        if (this.closeTimer) window.clearTimeout(this.closeTimer);
        if (this.stickyTransitionTimer) window.clearTimeout(this.stickyTransitionTimer);
        document.documentElement.classList.remove('ks-header-is-transitioning');
        this.headerSection?.classList.remove('ks-header-section--sticky');
        this.releasePage();
        this.initialized = false;
      }

      requestStickyUpdate() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.updateStickyState();
          this.scrollFrame = null;
        });
      }

      updateStickyState(immediate = false) {
        const shouldEnter = window.scrollY > this.stickyEnterThreshold;
        const shouldExit = window.scrollY <= this.stickyExitThreshold;

        if (!shouldEnter && !shouldExit) return;

        const nextState = shouldEnter;
        if (nextState === this.isSticky && this.classList.contains('is-scrolled') === nextState) return;

        this.isSticky = nextState;
        if (immediate) this.classList.add('is-initializing');
        if (!immediate) this.beginStickyTransition();
        this.classList.toggle('is-scrolled', nextState);

        if (immediate) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.classList.remove('is-initializing'));
          });
        }
      }

      syncStickyMetrics() {
        if (!this.utility) return;

        const utilityHeight = Math.ceil(this.utility.scrollHeight);
        if (utilityHeight > 0) {
          this.style.setProperty('--ks-header-utility-height', `${utilityHeight}px`);
        }
      }

      beginStickyTransition() {
        if (this.stickyTransitionTimer) window.clearTimeout(this.stickyTransitionTimer);

        document.documentElement.classList.add('ks-header-is-transitioning');
        this.stickyTransitionTimer = window.setTimeout(() => {
          document.documentElement.classList.remove('ks-header-is-transitioning');
          this.stickyTransitionTimer = null;
        }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 720);
      }

      setToggleState(button, expanded) {
        const target = document.getElementById(button.getAttribute('aria-controls'));
        button.setAttribute('aria-expanded', String(expanded));
        button.setAttribute('aria-label', expanded ? button.dataset.closeLabel : button.dataset.openLabel);
        if (target) target.hidden = !expanded;
        return target;
      }

      toggleDesktopSubmenu(button) {
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        this.closeDesktopSubmenus(button);
        this.setToggleState(button, willOpen);
        button.closest('.ks-header__desktop-item')?.classList.toggle('is-open', willOpen);
        this.lastSubmenuButton = willOpen ? button : null;
      }

      closeDesktopSubmenus(exceptButton = null) {
        this.querySelectorAll('[data-ks-submenu-toggle][aria-expanded="true"]').forEach((button) => {
          if (button === exceptButton) return;
          this.setToggleState(button, false);
          button.closest('.ks-header__desktop-item')?.classList.remove('is-open');
        });
      }

      toggleMobileSubmenu(button) {
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        this.setToggleState(button, willOpen);
      }

      openDrawer() {
        if (!this.drawer || !this.openButton) return;
        if (this.closeTimer) {
          window.clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
        this.lastFocusedElement = document.activeElement;
        this.drawer.hidden = false;
        this.openButton.setAttribute('aria-expanded', 'true');
        this.openButton.setAttribute('aria-label', 'Close menu');
        document.documentElement.classList.add('ks-header-menu-open');
        this.makePageInert(true);
        requestAnimationFrame(() => {
          this.drawer.classList.add('is-visible');
          this.closeButton?.focus();
        });
      }

      closeDrawer(restoreFocus = true) {
        if (!this.drawer || this.drawer.hidden) return;
        this.drawer.classList.remove('is-visible');
        this.openButton?.setAttribute('aria-expanded', 'false');
        this.openButton?.setAttribute('aria-label', 'Open menu');
        this.releasePage();

        this.closeTimer = window.setTimeout(() => {
          this.drawer.hidden = true;
          this.closeTimer = null;
          if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus();
        }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 240);
      }

      makePageInert(isInert) {
        const headerSection = this.closest('.shopify-section');
        [...document.body.children].forEach((element) => {
          if (element === headerSection || element.contains(headerSection)) return;
          if (isInert) {
            element.dataset.ksWasInert = String(element.inert);
            element.inert = true;
          }
        });
      }

      releasePage() {
        document.documentElement.classList.remove('ks-header-menu-open');
        document.querySelectorAll('[data-ks-was-inert]').forEach((element) => {
          element.inert = element.dataset.ksWasInert === 'true';
          delete element.dataset.ksWasInert;
        });
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) this.closeDesktopSubmenus();
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          if (this.drawer && !this.drawer.hidden) {
            this.closeDrawer();
          } else {
            this.closeDesktopSubmenus();
            if (this.lastSubmenuButton instanceof HTMLElement) {
              this.lastSubmenuButton.focus();
              this.lastSubmenuButton = null;
            }
          }
        }

        if (event.key === 'Tab' && this.drawer && !this.drawer.hidden) this.trapFocus(event);
      }

      trapFocus(event) {
        const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => !element.closest('[hidden]'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleResize() {
        if (this.dataset.sticky === 'true') {
          this.syncStickyMetrics();
          this.updateStickyState(true);
        }

        if (window.innerWidth > 1024) {
          if (this.closeTimer) {
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
          }
          this.closeDrawer(false);
        }
      }
    }
  );
}
